import React, { useState } from "react";
import { Loader2, LogOut, Search, RefreshCw, Download, Users, Inbox, CalendarDays, Settings } from "lucide-react";
import { CONFIG } from "@/config";
import { useAdminSession, useLeads } from "@/hooks/useAdmin";
import { LoginView } from "@/components/admin/LoginView";
import { StatCards, LeadsTable, LeadCards, countLeads, isGuideLead } from "@/components/admin/LeadViews";
import { ContactsView } from "@/components/admin/ContactsView";
import { AppointmentsView } from "@/components/admin/AppointmentsView";
import { SettingsView } from "@/components/admin/SettingsView";
import { DigestPanel } from "@/components/admin/DigestPanel";

const TABS = [
  { key: "contacts", label: "Contacts", icon: Users },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "leads", label: "Enquiries", icon: Inbox },
  { key: "settings", label: "Availability", icon: Settings },
];

const matchesFilter = (lead, filter) => {
  if (filter === "all") return true;
  if (filter === "guides") return isGuideLead(lead);
  return (lead.status || "new") === filter;
};

const matchesQuery = (lead, q) => {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return [lead.name, lead.email, lead.mobile, lead.primary_goal, lead.source].some((v) => (v || "").toLowerCase().includes(s));
};

const Toolbar = ({ shown, total, q, onQuery, onRefresh, loading }) => (
  <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
    <p className="text-sm text-ink/55" data-testid="lead-count">Showing <strong className="text-navy">{shown}</strong> of {total}</p>
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
        <input
          data-testid="lead-search"
          value={q}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search name, email, phone..."
          className="w-full rounded-full border border-navy/10 bg-white py-2.5 pl-10 pr-4 text-sm text-navy outline-none focus:border-royal focus:ring-2 focus:ring-royal/20 sm:w-72"
        />
      </div>
      <button data-testid="lead-refresh" onClick={onRefresh} aria-label="Refresh" className="flex h-11 w-11 items-center justify-center rounded-full border border-navy/10 bg-white text-navy transition-colors hover:bg-navy/[0.03]">
        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  </div>
);

const LeadsView = ({ onUnauthorized }) => {
  const { leads, loading, exporting, load, setStatus, exportCsv, replaceLead } = useLeads(onUnauthorized);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const filtered = leads.filter((l) => matchesFilter(l, filter) && matchesQuery(l, q));
  return (
    <>
      <div className="flex justify-end">
        <button data-testid="lead-export-csv" onClick={exportCsv} disabled={exporting || leads.length === 0} className="inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0d2c56] disabled:opacity-50">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
        </button>
      </div>
      <div className="mt-4"><StatCards counts={countLeads(leads)} filter={filter} onFilter={setFilter} /></div>
      <Toolbar shown={filtered.length} total={leads.length} q={q} onQuery={setQ} onRefresh={load} loading={loading} />
      <div className="mt-8">
        {loading ? (
          <div className="flex justify-center py-24 text-navy/40"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-navy/[0.08] bg-white py-24 text-center text-ink/50" data-testid="leads-empty">
            No enquiries {q || filter !== "all" ? "match this filter" : "yet"}.
          </div>
        ) : (
          <>
            <LeadsTable leads={filtered} onStatus={setStatus} onLogged={replaceLead} />
            <LeadCards leads={filtered} onStatus={setStatus} onLogged={replaceLead} />
          </>
        )}
      </div>
    </>
  );
};

const DashboardView = ({ user, onLogout }) => {
  const [tab, setTab] = useState("contacts");

  return (
    <div className="min-h-screen bg-lightbg">
      <header className="sticky top-0 z-10 border-b border-navy/5 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <div className="flex items-center gap-3">
            <img src={CONFIG.logo} alt="Auxieron logo" className="h-10 w-auto" />
            <div className="hidden sm:block">
              <p className="font-display text-base font-bold text-navy">Auxieron CRM</p>
              <p className="text-xs text-ink/50">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy/[0.03]">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <nav className="border-b border-navy/5 bg-white">
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-5 sm:px-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === t.key ? "border-gold text-navy" : "border-transparent text-navy/50 hover:text-navy"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8" data-testid={`admin-panel-${tab}`}>
        {tab === "contacts" && <ContactsView onUnauthorized={onLogout} />}
        {tab === "appointments" && <AppointmentsView onUnauthorized={onLogout} />}
        {tab === "leads" && <LeadsView onUnauthorized={onLogout} />}
        {tab === "settings" && <div className="space-y-6"><DigestPanel onUnauthorized={onLogout} /><SettingsView onUnauthorized={onLogout} /></div>}
      </main>
    </div>
  );
};

export const AdminDashboard = () => {
  const { user, checking, login, logout } = useAdminSession();
  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-lightbg text-navy/40"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }
  return user ? <DashboardView user={user} onLogout={logout} /> : <LoginView onLogin={login} />;
};
